<header class="content-header">
  <h4 class="vision-h4">Create Tasks</h4>
  @if (data.project_id && includesTagsContainer) {
    <div class="project-context">
      <span test-id="project-context">{{ 'Project' | titlecase }}: {{ includesTagsContainer.label }}</span>
    </div>
  }
</header>

<div mat-dialog-content>
  <form [formGroup]="formGroup" class="create-group-form">
    @if (!data.project_id && currentUser) {
      <div class="task-criteria">
        <label aria-label="Select Project"> Select Project </label>
        <div class="mat-form-field">
          <project-picker
            #projectPicker
            test-id="project-picker"
            [filter]="getProjectsFilter()"
            (valueChange)="selectProject($event)"
            [validators]="[requiredValidator, projectHasDataValidator]"
          >
            @if (projectPicker.touched && projectPicker.errors?.required) {
              <mat-error> Project is required. </mat-error>
            }
            @if (projectPicker.touched && projectPicker.errors?.empty) {
              <mat-error>
                {{ projectPicker.errors.empty }}
              </mat-error>
            }
          </project-picker>
        </div>
      </div>
    }

    <div class="task-criteria">
      <label aria-label="Task Type"> Task Type </label>
      <mat-form-field appearance="outline">
        <mat-select test-id="task-type" formControlName="taskType">
          @for (taskType of taskTypes; track taskType) {
            <mat-option [value]="taskType.value">{{ taskType.label }}</mat-option>
          }
        </mat-select>
        @if (formGroup.controls.taskType.touched && formGroup.controls.taskType.hasError('required')) {
          <mat-error>{{ 'Task Type is required.' }}</mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Read protocols only display for "Reader" task type -->
    @if (formGroup.controls.taskType.value === 'R' && protocolOptions) {
      <div class="task-criteria">
        <label aria-label="Read Protocol"> Read Protocol </label>
        <mat-form-field appearance="outline">
          <mat-select test-id="read-protocol" formControlName="readProtocol" placeholder="Select Read Protocol">
            @for (protocol of protocolOptions; track protocol) {
              <mat-option [value]="protocol.value">{{ protocol.label }}</mat-option>
            }
          </mat-select>
          @if (formGroup.controls.readProtocol.hasError('required')) {
            <mat-error>Read protocol is required.</mat-error>
          }
        </mat-form-field>
        @if (protocolOptions.length === 0) {
          <div class="read-protocol-warning">
            @if (getProjectLabel(formGroup.controls.project.value); as projectLabel) {
              <mat-icon class="xs orange">warning</mat-icon
              ><i
                >{{"No read protocols were found for project labeled '{{0}}'. See documentation for details to add a
                protocol for read tasks." | interpolate:projectLabel }}</i
              >
            } @else {
              @if (projectWasTouched()) {
                <mat-icon class="xs orange">warning</mat-icon
                ><i>No read protocols were found. See documentation for details to add a protocol for read tasks.</i>
              }
            }
          </div>
        }
      </div>
    }

    <div class="task-criteria">
      <label aria-label="Container"> Container </label>
      <mat-form-field appearance="outline">
        <mat-select test-id="fw-object-select" formControlName="container" placeholder="Select Container">
          @for (container of containerTypes; track container) {
            <mat-option [value]="container.value">{{ container.label }}</mat-option>
          }
        </mat-select>
        @if (formGroup.controls.container.hasError('required')) {
          <mat-error>{{ 'createTaskDialog.error.containerRequired' | translate }}</mat-error>
        }
      </mat-form-field>
    </div>

    @if (includesTagsContainer) {
      <div class="full-width">
        <label aria-label="Container Filter"> Container Filter </label>
        <div>
          <mat-label>Contains tag(s)</mat-label>
          <fw-tags
            test-id="container-includes-tags"
            [useAsInlineField]="true"
            formControlName="containerIncludesTags"
            [container]="includesTagsContainer"
            [autoSave]="false"
          >
          </fw-tags>
        </div>
        <div>
          <mat-label>Does NOT contain tag(s) </mat-label>
          <fw-tags
            test-id="container-excludes-tags"
            [useAsInlineField]="true"
            formControlName="containerExcludesTags"
            [container]="excludesTagsContainer"
            [autoSave]="false"
          >
          </fw-tags>
        </div>
      </div>
    }

    @if (shouldFetchContainerCount$ | async) {
      <div class="container-count full-width">
        @if (!loadingMatchingContainers) {
          <strong class="blue" style="font-size: 16px">{{ matchingContainerCount }}</strong>
          <span>{{ selectedContainerLabel }} found</span>
        } @else {
          <mat-spinner diameter="16"></mat-spinner>
          <span>... searching for {{ selectedContainerLabel | lowercase }}</span>
        }
      </div>
    }

    <div class="task-criteria">
      <div class="flex-label">
        <label aria-label="Assignee"> Assignee </label>
        <mat-icon
          fwTooltip="This list displays user with the correct permissions to perform Read Tasks in the system."
          class="xs"
          >info</mat-icon
        >
      </div>
      <div class="mat-form-field">
        <app-typeahead
          #assigneeTypeahead
          test-id="user-typeahead"
          class="solid-background full-width"
          [disabled]="formGroup.get('assignee').disabled"
          placeholder="Assignee"
          [options]="userOptions"
          [validators]="[requiredAssigneeValidator, noAssigneesValidator]"
          (valueChange)="updateAssignee($event.toString())"
        >
        </app-typeahead>
      </div>
    </div>

    <div class="task-criteria">
      <label aria-label="Date Due"> Date Due </label>
      <mat-form-field appearance="outline" class="date-field flex-field">
        <input
          test-id="due-date-input"
          formControlName="dueDate"
          matInput
          [matDatepicker]="datepicker"
          placeholder="Select Date"
          autocomplete="off"
        />
        <mat-datepicker-toggle [for]="datepicker" test-id="due-date-picker-toggle"></mat-datepicker-toggle>
        <mat-datepicker #datepicker></mat-datepicker>
        @if (formGroup.controls.dueDate.hasError('required')) {
          <mat-error>Date Due is required.</mat-error>
        }
        @if (formGroup.controls.dueDate.hasError('dateIsPast')) {
          <mat-error>Date Due must be a future date.</mat-error>
        }
      </mat-form-field>
    </div>

    @if (taskTagsContainer) {
      <div class="full-width">
        <label>Task Tags</label>
        <fw-tags
          [allowCustomTags]="true"
          [useAsInlineField]="true"
          formControlName="tags"
          [container]="taskTagsContainer"
          [autoSave]="false"
        >
        </fw-tags>
      </div>
    }
  </form>
</div>

<div class="action-buttons" mat-dialog-actions align="end">
  <div class="footer-create-more">
    <mat-checkbox test-id="create-more-checkbox" [(ngModel)]="createMore"></mat-checkbox>
    <span>Create More Tasks</span>
  </div>
  <button test-id="close-button" mat-stroked-button (click)="cancel()">Cancel</button>
  <button test-id="create-button" mat-flat-button color="primary" (click)="submit()" [disabled]="formGroup.invalid">
    Create
  </button>
</div>
